(ns practitest.frontend.integration-settings.auto-sync-tabs
  (:require [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.i18n :refer [format-translation get-translation]]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.selector :as selector2]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.unsaved-changes-confirmation-modal :as unsaved-changes-modal]
            [practitest.frontend.components2.info-note :as info-note]
            [practitest.frontend.init :as init]
            [practitest.frontend.integration-settings.auto-sync-filters.events :as events]
            [practitest.frontend.integration-settings.auto-sync-filters.subs :as subs]
            [practitest.frontend.integration-settings.sub-tabs :as sub-tabs]))

(def ^:private unsynced-projects-note
  "PractiTest will only sync issues from Jira projects that are synced to PractiTest.")

(def ^:private delete-behavior-note
  (str "If you delete the Jira filter synchronization from PractiTest or delete the Filter "
       "from Jira, the Jira issues which are already in PractiTest will keep syncing."))

(defn- target-items []
  [{:id "Issues"       :label (get-translation "issue.plural" "Issues")}
   {:id "Requirements" :label (get-translation "requirement.plural" "Requirements")}])

(defn- selected-item [items id]
  (first (filter #(= (:id %) id) items)))

(defn- row-select [{:keys [items id placeholder on-change]}]
  [selector2/new-select
   { :value       (selected-item items id)
     :items       items
     :key-fn      :id
     :label-fn    :label
     :placeholder placeholder
     :strategy    "fixed"
     :on-change   on-change}])

(defn- filter-select [idx row all-filters]
  [row-select
   {:items       all-filters
    :id          (:filter-id row)
    :placeholder (get-translation "jira-auto-sync-filters.select-filter" "Select filter")
    :on-change   (fn [item] (re-frame/dispatch [::events/set-filter idx (:id item)]))}])

(defn- target-select [idx row]
  [row-select
   {:items       (target-items)
    :id          (:filter-target row)
    :placeholder (get-translation "jira-auto-sync-filters.select-entity" "Select entity")
    :on-change   (fn [item] (re-frame/dispatch [::events/set-target idx (:id item)]))}])

(defn- table-row [idx row all-filters]
  ^{:key idx}
  [:tr
   [:td (if (:editable row)
          [filter-select idx row all-filters]
          [:p.auto-sync-cell-text (:filter-name row)])]
   [:td (if (:editable row)
          [target-select idx row]
          [:p.auto-sync-cell-text (:filter-target row)])]
   [:td [:p.auto-sync-cell-text (:user-name row)]]
   [:td [:p.auto-sync-cell-text (:last-sync row)]]
   [:td [:p.auto-sync-cell-text (:sync-interval row)]]
   [:td.text-center.align-middle
    [buttons/icon-button
     {:icon     [icons/mux-trash]
      :on-click #(re-frame/dispatch [::events/remove-row idx])}]]])

(defn- empty-state-view []
  [:div.auto-sync-empty-state
   [:div.empty-state-icon [icons/mux-edit-columns]]
   [:p.empty-title (get-translation "jira-auto-sync-filters.empty-state.title" "No Filters yet")]
   [:p.empty-subtitle (get-translation "jira-auto-sync-filters.empty-state.subtitle"
                                       "Add new filter auto sync to enhance your integration")]])

(defn- add-filter-row []
  ^{:key "add-filter"}
  [:tr.auto-sync-add-filter-row
   [:td {:col-span 6}
    [buttons/button
     { :text      (get-translation "jira-auto-sync-filters.add-new-filter" "Add Filter")
       :tertiary? true
       :icon      [icons/mux-plus-circle]
       :on-click  #(re-frame/dispatch [::events/add-row])}]]])

(defn- filters-table [rows all-filters sync-interval]
  [:div.auto-sync-table-wrapper
   [:table.table
    [:thead
     [:tr
      [:th (get-translation "jira-auto-sync-filters.col.filter" "Jira Filter")]
      [:th (get-translation "jira-auto-sync-filters.col.target" "To PractiTest's")]
      [:th (get-translation "jira-auto-sync-filters.col.created-by" "Created By")]
      [:th (get-translation "jira-auto-sync-filters.col.last-sync" "Last Sync Date")]
      [:th (get-translation "jira-auto-sync-filters.col.sync-interval" "Sync Interval")]
      [:th ""]]]
    [:tbody
     (if (empty? rows)
       [:tr [:td {:col-span 6} [empty-state-view]]]
       (doall
         (map-indexed
           (fn [idx row]
             (table-row idx (assoc row :sync-interval sync-interval) all-filters))
           rows)))
     [add-filter-row]]]])

(defn auto-sync-tabs-root [_]
  (let [show-cancel-confirm? (reagent/atom false)
        container-ref        (reagent/atom nil)]
    (fn [_]
      (let [initialized?  @(re-frame/subscribe [::subs/initialized?])
            rows          @(re-frame/subscribe [::subs/rows])
            all-filters   @(re-frame/subscribe [::subs/all-filters])
            warnings      @(re-frame/subscribe [::subs/warnings])
            sync-interval @(re-frame/subscribe [::subs/sync-interval])
            saving?       @(re-frame/subscribe [::subs/saving?])
            changes-made? @(re-frame/subscribe [::subs/changes-made?])
            cancel-url    @(re-frame/subscribe [::subs/cancel-url])
            show-confirm? @show-cancel-confirm?
            on-cancel     (fn []
                            (re-frame/dispatch [::events/cancel])
                            (if (seq cancel-url)
                              (dom/navigate! cancel-url)
                              (.back js/history)))]
        [:<>
         [sub-tabs/sub-tabs @(re-frame/subscribe [::subs/sub-tabs-props])]
         (if-not initialized?
           [:div.auto-sync-filters-loading
            [spinners/mux-spinner {:delay 200}]]
           [:div.auto-sync-filters-page {:ref (dom/ref-handler container-ref)}
            [:div.auto-sync-filters-content
             [info-note/mux-info-note
              {:message (get-translation "jira-auto-sync-filters.note.unsynced-projects" unsynced-projects-note)}]
             [info-note/mux-info-note
              {:message (get-translation "jira-auto-sync-filters.note.delete-behavior" delete-behavior-note)}]
             (doall
               (map-indexed
                 (fn [idx filter-name]
                   ^{:key idx}
                   [info-note/mux-info-note
                    {:class   "mux-info-note--error"
                     :icon    [icons/mux-warning]
                     :message (format-translation
                                (get-translation "jira-auto-sync-filters.warning.filter-not-in-favorites"
                                                 "Filter '%{filter_name}' no longer appears in your favorites filters.")
                                :filter_name filter-name)}])
                 warnings))
             [filters-table rows all-filters sync-interval]]

            [:div.auto-sync-filters-footer
             [:div.auto-sync-filters-footer-actions
              [buttons/button
               { :text     (get-translation "generic.button.cancel" "Cancel")
                 :on-click (fn []
                             (if changes-made?
                               (reset! show-cancel-confirm? true)
                               (on-cancel)))}]
              [buttons/button
               { :text      (if saving?
                              (get-translation "integration-field-mapping.saving" "Saving...")
                              (get-translation "integration-field-mapping.update-settings" "Update Settings"))
                 :primary?  (boolean changes-made?)
                 :disabled? (or saving? (not changes-made?))
                 :on-click  #(re-frame/dispatch [::events/save])}]]]

            [unsaved-changes-modal/unsaved-changes-confirmation-modal
             {:show?         show-confirm?
              :container-ref container-ref
              :on-close      #(reset! show-cancel-confirm? false)
              :on-confirm    (fn []
                               (reset! show-cancel-confirm? false)
                               (on-cancel))}]])]))))

(defn ^:dev/after-load remount []
  (init/remount-roots "integration-settings-auto-sync-tabs-component"
                      auto-sync-tabs-root))

(defn ^:export init [webcomponent options]
  (init/init! {:options       options
               :init-event-fn (fn [config] [::events/initialize-db config])
               :webcomponent  webcomponent
               :root-view     auto-sync-tabs-root}))

(utils/register-webcomponent! "integration-settings-auto-sync-tabs-component")
